import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Plus, 
  UserPlus,
  Users,
  MoreHorizontal,
  Trash2,
  Target,
  Building2,
  TrendingUp,
  Eye,
  EyeOff,
  ArrowLeft,
  FileText,
  ExternalLink
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ConsultantDetailView } from '../components/consultants/ConsultantDetailView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Consultants() {
  const [consultants, setConsultants] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [consultantDetails, setConsultantDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [consultantsRes, statsRes] = await Promise.all([
        axios.get(`${API}/consultants`, { withCredentials: true }),
        axios.get(`${API}/consultants/stats`, { withCredentials: true })
      ]);
      setConsultants(consultantsRes.data);
      
      // Convert stats array to object keyed by consultant id
      const statsMap = {};
      statsRes.data.forEach(s => {
        statsMap[s.consultant_id] = s;
      });
      setStats(statsMap);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    try {
      await axios.post(`${API}/consultants`, {
        ...formData,
        role: 'consultant'
      }, { withCredentials: true });
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleDelete = async (consultantId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce consultant ?')) return;
    try {
      await axios.delete(`${API}/consultants/${consultantId}`, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '' });
    setError('');
  };

  const getConsultantStats = (consultantId) => {
    return stats[consultantId] || { clients_count: 0, keywords_count: 0, keywords_improved: 0 };
  };

  const handleConsultantClick = async (consultant) => {
    setSelectedConsultant(consultant);
    setDetailsLoading(true);
    try {
      const { data } = await axios.get(`${API}/consultants/${consultant.id}/details`, { withCredentials: true });
      setConsultantDetails(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setDetailsLoading(false);
    }
  };

  // If a consultant is selected, show detail view
  if (selectedConsultant) {
    return (
      <Layout>
        <ConsultantDetailView
          consultant={selectedConsultant}
          details={consultantDetails}
          loading={detailsLoading}
          onBack={() => { setSelectedConsultant(null); setConsultantDetails(null); }}
        />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="consultants-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Consultants SEO
            </h1>
            <p className="text-neutral-500 mt-1">Gérez votre équipe et suivez leurs performances</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#002FA7] hover:bg-[#002277] text-white"
                data-testid="add-consultant-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Ajouter un consultant
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Nouveau consultant</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                    {error}
                  </div>
                )}
                <div>
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jean Dupont"
                    required
                    className="mt-1"
                    data-testid="consultant-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="consultant@example.com"
                    required
                    className="mt-1"
                    data-testid="consultant-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Mot de passe *</Label>
                  <div className="relative mt-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      required
                      className="pr-10"
                      data-testid="consultant-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setDialogOpen(false); resetForm(); }}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-[#002FA7] hover:bg-[#002277]"
                    data-testid="save-consultant-btn"
                  >
                    Créer le compte
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Consultants Grid */}
        {loading ? (
          <div className="p-8 text-center" data-testid="consultants-loading">
            <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
          </div>
        ) : consultants.length === 0 ? (
          <div className="bg-white border border-neutral-200 p-12 text-center" data-testid="no-consultants">
            <Users className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-500">Aucun consultant dans l'équipe</p>
            <p className="text-sm text-neutral-400 mt-1">Ajoutez votre premier consultant SEO</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="consultants-grid">
            {consultants.map((consultant) => {
              const consultantStats = getConsultantStats(consultant.id);
              
              return (
                <div 
                  key={consultant.id} 
                  className="bg-white border border-neutral-200 hover:border-[#002FA7]/40 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => handleConsultantClick(consultant)}
                  data-testid={`consultant-card-${consultant.id}`}
                >
                  <div className="p-4 border-b border-neutral-100">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#002FA7]/10 rounded-full flex items-center justify-center">
                          <span className="text-[#002FA7] font-semibold">
                            {consultant.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-neutral-900">{consultant.name}</h3>
                          <p className="text-sm text-neutral-500">{consultant.email}</p>
                        </div>
                      </div>
                      {consultant.role !== 'admin' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem 
                              onClick={() => handleDelete(consultant.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div className="flex items-center justify-center gap-1 text-neutral-500 mb-1">
                          <Building2 className="w-3 h-3" />
                          <span className="text-xs">Clients</span>
                        </div>
                        <p className="font-mono text-xl font-bold text-neutral-900">
                          {consultantStats.clients_count}
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center justify-center gap-1 text-neutral-500 mb-1">
                          <Target className="w-3 h-3" />
                          <span className="text-xs">Mots-clés</span>
                        </div>
                        <p className="font-mono text-xl font-bold text-neutral-900">
                          {consultantStats.keywords_count}
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center justify-center gap-1 text-neutral-500 mb-1">
                          <TrendingUp className="w-3 h-3" />
                          <span className="text-xs">Améliorés</span>
                        </div>
                        <p className="font-mono text-xl font-bold text-green-600">
                          +{consultantStats.keywords_improved}
                        </p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-neutral-100">
                      <span className={`text-xs font-mono px-2 py-1 rounded ${
                        consultant.role === 'admin' 
                          ? 'bg-[#002FA7]/10 text-[#002FA7]' 
                          : 'bg-neutral-100 text-neutral-600'
                      }`}>
                        {consultant.role === 'admin' ? 'Administrateur' : 'Consultant'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
